import { z } from 'zod';

import { HttpStatus } from '../api-codes/status-codes';

export type HwApiErrorResponse = z.infer<typeof hwApiErrorResponse>;
export type HwApiNotFoundResponse = z.infer<typeof hwApiNotFoundResponse>;

/**
 * Hunkware API Error Response Schema
 *
 * This schema is used to represent an error response from the Hunkware API.
 */
export const hwApiErrorResponse = z.object({
  statusCode: z.number(),
  message: z.string(),
  error: z.string(),
  path: z.string().optional(),
  details: z.record(z.any()).optional(),
});

/**
 * Hunkware API Bad Request Response Schema (400)
 *
 * This schema is used to represent a bad request error response from the Hunkware API.
 */
export const hwApiBadRequestResponse = hwApiErrorResponse.extend({
  statusCode: z.literal(HttpStatus.BAD_REQUEST),
});

/**
 * Hunkware API Unauthorized Response Schema (401)
 *
 * This schema is used to represent an unauthorized error response from the Hunkware API.
 */
export const hwApiUnauthorizedResponse = hwApiErrorResponse.extend({
  statusCode: z.literal(HttpStatus.UNAUTHORIZED),
});

/**
 * Hunkware API Forbidden Response Schema (403)
 *
 * This schema is used to represent a forbidden error response from the Hunkware API.
 */
export const hwApiForbiddenResponse = hwApiErrorResponse.extend({
  statusCode: z.literal(HttpStatus.FORBIDDEN),
});

/**
 * Hunkware API Not Found Response Schema (404)
 *
 * This schema is used to represent a not found error response from the Hunkware API.
 */
export const hwApiNotFoundResponse = hwApiErrorResponse.extend({
  statusCode: z.literal(HttpStatus.NOT_FOUND),
});

/**
 * Hunkware API Conflict Response Schema (409)
 *
 * This schema is used to represent a conflict error response from the Hunkware API.
 */
export const hwApiConflictResponse = hwApiErrorResponse.extend({
  statusCode: z.literal(HttpStatus.CONFLICT),
});

/**
 * Hunkware API Internal Server Error Response Schema (500)
 *
 * This schema is used to represent an internal server error response from the Hunkware API.
 */
export const hwApiInternalServerErrorResponse = hwApiErrorResponse.extend({
  statusCode: z.literal(HttpStatus.INTERNAL_SERVER_ERROR),
});

/**
 * Standard common responses shared across all TS-Rest contracts.
 * Use as `commonResponses` in RouterOptions.
 */
export const HW_COMMON_RESPONSES = {
  400: hwApiBadRequestResponse,
  401: hwApiUnauthorizedResponse,
  403: hwApiForbiddenResponse,
} as const;
